import React, { createContext, useContext, useEffect, useState } from "react";
import {
  THEMES,
  applyTheme,
  getThemeByKey,
  loadSavedThemeKey,
  loadOrgThemeKey,
  saveThemeKey,
  saveOrgThemeKey,
  updateFavicon,
} from "../utils/themeManager";
import { getActiveOrgTheme, getAllThemes } from "../services/SassServices/ThemeService";

export const BrandContext = createContext();

export const BrandProvider = ({ children }) => {
  const [activeKey, setActiveKey] = useState(() => loadSavedThemeKey());

  // Apply on first mount and whenever key changes
  useEffect(() => {
    applyTheme(activeKey);
  }, [activeKey]);

  /** Platform admin: select global theme. Accepts a known theme key, or a full theme object (e.g. fetched from the backend). */
  const selectTheme = (keyOrTheme) => {
    const theme = typeof keyOrTheme === "string" ? getThemeByKey(keyOrTheme) : keyOrTheme;
    if (!theme?.dark || !theme?.key) return;
    setActiveKey(theme.key);
    saveThemeKey(theme.key);
    applyTheme(theme);
  };

  /** CLIENT org user: select and persist their own org's theme. Accepts a known theme key, or a full theme object (e.g. fetched from the backend). */
  const selectOrgTheme = (orgId, keyOrTheme) => {
    const theme = typeof keyOrTheme === "string" ? getThemeByKey(keyOrTheme) : keyOrTheme;
    if (!theme?.dark || !theme?.key) return;
    setActiveKey(theme.key);
    saveOrgThemeKey(orgId, theme.key);
    applyTheme(theme);
  };

  /**
   * Resolve and apply the org's assigned theme (called at login/context-switch for
   * CLIENT org users, and for PLATFORM users viewing a specific client). Applies the
   * cached key immediately (instant paint, no flash of the wrong theme), then calls
   * both the full theme catalogue (getAllThemes) and the org's assigned theme
   * (getActiveOrgTheme) in parallel — same two calls Saas/branding/index.js makes —
   * so the applied theme is cross-referenced against the catalogue for complete,
   * consistent color data, and re-applies + re-caches it if it differs from cache.
   */
  const applyOrgTheme = async (orgId) => {
    const cachedKey = loadOrgThemeKey(orgId);
    setActiveKey(cachedKey);
    applyTheme(cachedKey);

    if (orgId == null) return;
    try {
      const [allThemes, orgTheme] = await Promise.all([
        getAllThemes(),
        getActiveOrgTheme(orgId),
      ]);
      const resolved =
        (orgTheme &&
          allThemes.find(
            (t) => t.themeId === orgTheme.themeId || t.key === orgTheme.key,
          )) ||
        orgTheme;
      if (resolved?.key && resolved.key !== cachedKey) {
        setActiveKey(resolved.key);
        applyTheme(resolved);
        saveOrgThemeKey(orgId, resolved.key);
      }
    } catch (_) {
      // Keep the cached theme applied — org theme fetch failing shouldn't break the app.
    }
  };

  /**
   * Platform login (no client org in context) or a platform admin leaving a
   * client's context: restore the platform-wide theme. Applies the cached
   * key immediately (instant paint, same as applyOrgTheme), then fetches the
   * platform's actual default-active theme from the backend and re-applies
   * + re-caches it if it differs from what was cached — mirrors
   * applyOrgTheme's cached-first-then-API pattern so a platform login
   * reflects whatever theme is currently set as default, not just whatever
   * happened to be cached on this device.
   */
  const resetToPlatformTheme = async () => {
    const cachedKey = loadSavedThemeKey();
    setActiveKey(cachedKey);
    applyTheme(cachedKey);

    try {
      const allThemes = await getAllThemes();
      const active = allThemes.find((t) => t.active);
      if (active?.key && active.key !== cachedKey) {
        setActiveKey(active.key);
        applyTheme(active);
        saveThemeKey(active.key);
      }
    } catch (_) {
      // Keep the cached theme applied — platform theme fetch failing shouldn't break the app.
    }
  };

  return (
    <BrandContext.Provider
      value={{
        activeKey,
        activeTheme: getThemeByKey(activeKey),
        themes: THEMES,
        selectTheme,
        selectOrgTheme,
        applyOrgTheme,
        resetToPlatformTheme,
        updateFavicon,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);
